import { VOCATION_SUBINDUSTRY, type VocationSubindustry } from "../types/vocationSubindustry";
import { EDUCATION_LEVEL, type EducationLevel } from "../types/customerDetail";
import { PROGRAM_LEVEL } from "../types/program";
import { isOptionTargetQuestion, type FormFunnelOptionTarget } from "./formFunnelQuestionTargets";

export const FORM_FUNNEL_ZIP_FIELD_KEY = "ZIP_CODE";
export const FORM_FUNNEL_SUBINDUSTRY_FILTER_KEY = "program_vocation_subindustries_in";
export const FORM_FUNNEL_VOCATION_FILTER_KEY = "program_vocation_in";

/**
 * Filter keys whose answers are recorded with the lead but never affect which
 * programs match: `processFormAnswers` builds its METADATA_ONLY_KEYS from this
 * list and derives no filter or metadata from these two (unlike
 * `education_level`, the third metadata-only key, which feeds
 * `metadata.educationLevel` and does constrain results). Consumed by
 * `buildConcentrationAvailabilityAnswers` to keep availability-inert answers
 * out of the query variables — sharing the constant is what makes the client
 * projection and the server derivation unable to drift apart.
 */
export const AVAILABILITY_INERT_FILTER_KEYS = [
  "timeframe_preference",
  "enroll_likelihood",
] as const;

const AVAILABILITY_INERT_FILTER_KEY_SET = new Set<string>(AVAILABILITY_INERT_FILTER_KEYS);

/**
 * The customer fields whose answers feed availability-relevant metadata in
 * `processFormAnswers` (`metadata.educationLevel` / `metadata.gradYear`). A
 * question with an *enabled* CMS mapping to one of these constrains results
 * whatever its filter key, so the inert projection must keep its answer;
 * mappings to any other customer field only populate the lead and are
 * availability-inert. Pinned against the server's actual derivation by the
 * coupling tests in processFormAnswers.test.ts.
 */
export const AVAILABILITY_METADATA_CUSTOMER_FIELDS = [
  "educationLevel",
  "graduatedHighSchoolIn",
] as const;

const AVAILABILITY_METADATA_CUSTOMER_FIELD_SET = new Set<string>(
  AVAILABILITY_METADATA_CUSTOMER_FIELDS
);

/**
 * The canonical fieldKeys through which the server's HOSTED_STEP_FALLBACKS
 * table can feed availability metadata with no CMS mapping at all
 * (`getMappedCustomerField` falls back by fieldKey when the mapping is absent
 * or disabled). A question outside this list with no filter key and no enabled
 * availability mapping is invisible to `processFormAnswers`. Pinned to the
 * server table by a coupling test in processFormAnswers.test.ts.
 */
export const AVAILABILITY_METADATA_FALLBACK_FIELD_KEYS = ["EDUCATION_LEVEL", "GRAD_YEAR"] as const;

const AVAILABILITY_METADATA_FALLBACK_FIELD_KEY_SET = new Set<string>(
  AVAILABILITY_METADATA_FALLBACK_FIELD_KEYS
);

/**
 * The available concentration facets for a set of answers + ZIP, as returned by
 * `getFormFunnelAvailableConcentrations`. A concentration question is filtered
 * against whichever facet matches its granularity: subindustry-keyed questions
 * use `subindustries`; vocation-keyed (`program_vocation_in`) questions use
 * `vocationIds`.
 */
export interface ConcentrationAvailability {
  subindustries?: readonly string[] | null;
  vocationIds?: readonly string[] | null;
  optionAvailability?:
    | readonly {
        fieldKey: string;
        optionValues: readonly string[];
      }[]
    | null;
}

export type QuestionLike = {
  id?: string | null;
  fieldKey?: string | null;
  type?: string | null;
  filterMapping?: {
    mode?: "question" | "option" | null;
    filterKey?: string | null;
    valueMapping?: Record<string, string> | null;
    staticOptions?: boolean | null;
  } | null;
  customerFieldMapping?: {
    enabled?: boolean | null;
    customerField?: string | null;
  } | null;
};

type QuestionWithOptionsLike = QuestionLike & {
  options?: { value: string | number | boolean; target?: FormFunnelOptionTarget | null }[] | null;
};

/**
 * The key a question's answer lives under in the answers map. Two question
 * shapes flow through this module: Payload's `PayloadFormQuestion` (answer key
 * on `fieldKey`; `id` is the Payload document id) and the UI's `QuestionConfig`
 * (answer key on `id`; no `fieldKey` field). Preferring `fieldKey` with an `id`
 * fallback is correct for both, so every answer-reading function here accepts
 * either collection interchangeably.
 */
const getAnswerKey = (question: QuestionLike): string | null =>
  question.fieldKey ?? question.id ?? null;

export function getFormFunnelEducationLevel(
  questions: readonly QuestionLike[],
  answers: Record<string, unknown>
): EducationLevel | undefined {
  let educationLevel: EducationLevel | undefined;
  for (const question of questions) {
    const answerKey = getAnswerKey(question);
    // CMS mappings take precedence over the canonical EDUCATION_LEVEL fallback.
    const customerField =
      question.customerFieldMapping?.enabled && question.customerFieldMapping.customerField
        ? question.customerFieldMapping.customerField
        : answerKey === "EDUCATION_LEVEL"
          ? "educationLevel"
          : undefined;
    if (
      !answerKey ||
      isOptionTargetQuestion(question) ||
      (customerField !== "educationLevel" &&
        question.filterMapping?.filterKey !== "education_level")
    ) {
      continue;
    }
    const answer = answers[answerKey];
    if (answer === undefined || answer === null || answer === "" || Array.isArray(answer)) continue;
    const mappedAnswer = question.filterMapping?.valueMapping?.[String(answer)] ?? answer;
    if (Object.values(EDUCATION_LEVEL).includes(mappedAnswer as EducationLevel)) {
      educationLevel = mappedAnswer as EducationLevel;
    }
  }
  return educationLevel;
}

const SUB_BACHELOR_EDUCATION_LEVELS: readonly EducationLevel[] = [
  EDUCATION_LEVEL.NO_HS_DIPLOMA_GED,
  EDUCATION_LEVEL.CURRENT_HIGH_SCHOOL,
  EDUCATION_LEVEL.HIGH_SCHOOL_DIPLOMA_GED,
  EDUCATION_LEVEL.SOME_COLLEGE,
  EDUCATION_LEVEL.ASSOCIATE,
];

/** Unanswered education leaves graduate options and matching unrestricted. */
export const isGraduateProgramEligible = (educationLevel: EducationLevel | undefined): boolean =>
  !educationLevel || !SUB_BACHELOR_EDUCATION_LEVELS.includes(educationLevel);

export function filterDegreeOptionsByEducation<T extends QuestionWithOptionsLike>(
  question: T,
  educationLevel: EducationLevel | undefined
): T {
  if (
    isOptionTargetQuestion(question) ||
    question.filterMapping?.filterKey !== "program_level_in" ||
    isGraduateProgramEligible(educationLevel)
  ) {
    return question;
  }
  return {
    ...question,
    options: question.options?.filter(
      (option) =>
        (question.filterMapping?.valueMapping?.[String(option.value)] ?? option.value) !==
        PROGRAM_LEVEL.GRADUATE
    ),
  };
}

export function removeIneligibleDegreeAnswers<T extends QuestionWithOptionsLike>(
  questions: readonly T[],
  answers: Record<string, unknown>
): Record<string, unknown> {
  const educationLevel = getFormFunnelEducationLevel(questions, answers);
  if (isGraduateProgramEligible(educationLevel)) return answers;
  return removeUnavailableQuestionAnswers(
    questions
      .filter(
        (question) =>
          !isOptionTargetQuestion(question) &&
          question.filterMapping?.filterKey === "program_level_in"
      )
      .map((question) => filterDegreeOptionsByEducation(question, educationLevel)),
    answers
  );
}

const VOCATION_SUBINDUSTRY_VALUES = new Set<string>(Object.values(VOCATION_SUBINDUSTRY));

export function isVocationSubindustryValue(value: unknown): value is VocationSubindustry {
  return typeof value === "string" && VOCATION_SUBINDUSTRY_VALUES.has(value);
}

/**
 * A ZIP question is identified by the canonical `ZIP_CODE` field key or by the
 * `zip` DreamSearch filter key (the marker that its answer feeds geo scoring).
 */
export function isFormFunnelZipQuestion(question: QuestionLike | null | undefined): boolean {
  if (!question) return false;
  if (isOptionTargetQuestion(question)) return false;
  return (
    getAnswerKey(question) === FORM_FUNNEL_ZIP_FIELD_KEY ||
    question.filterMapping?.filterKey === "zip"
  );
}

/**
 * Legacy concentration questions are identified by their DreamSearch filter
 * key, which maps options to the corresponding availability facet:
 * - `program_vocation_subindustries_in` — options map to subindustries.
 * - `program_vocation_in` — options map to vocation IDs (via `valueMapping`).
 * Option-mode questions use their server-projected stable option values so
 * industry targets and grouped targets retain their complete native semantics.
 */
export function isFormFunnelConcentrationQuestion(
  question: QuestionLike | null | undefined
): boolean {
  if (isOptionTargetQuestion(question)) return true;
  const filterKey = question?.filterMapping?.filterKey;
  return (
    filterKey === FORM_FUNNEL_SUBINDUSTRY_FILTER_KEY ||
    filterKey === FORM_FUNNEL_VOCATION_FILTER_KEY
  );
}

export function isDynamicConcentrationQuestion(question: QuestionLike | null | undefined): boolean {
  return (
    isFormFunnelConcentrationQuestion(question) && question?.filterMapping?.staticOptions !== true
  );
}

export function findFirstDynamicConcentrationQuestionIndex(questions: QuestionLike[]): number {
  return questions.findIndex(isDynamicConcentrationQuestion);
}

export function hasUpstreamZipQuestion(questions: QuestionLike[], questionIndex: number): boolean {
  if (questionIndex <= 0) return false;
  return questions.slice(0, questionIndex).some(isFormFunnelZipQuestion);
}

export function getZipAnswerFromFormAnswers(
  questions: QuestionLike[],
  answers: Record<string, unknown>
): string | null {
  for (const question of questions) {
    const answerKey = getAnswerKey(question);
    if (!isFormFunnelZipQuestion(question) || !answerKey) continue;
    const answer = answers[answerKey];
    if (typeof answer === "string" && answer.trim()) {
      return answer.trim();
    }
  }
  return null;
}

export function filterConcentrationOptionsByAvailability<T extends QuestionWithOptionsLike>(
  question: T,
  availability: ConcentrationAvailability
): T {
  if (!isDynamicConcentrationQuestion(question)) return question;
  if (isOptionTargetQuestion(question)) {
    const available = availability.optionAvailability?.find(
      (entry) => entry.fieldKey === getAnswerKey(question)
    );
    // Same fail-open display contract as legacy facet filtering. Matching
    // independently uses the throwing candidate query to distinguish an error.
    if (!available?.optionValues.length) return question;
    const optionValues = new Set(available.optionValues);
    const options = question.options?.filter((option) => optionValues.has(String(option.value)));
    return options?.length ? { ...question, options } : question;
  }
  const filterKey = question.filterMapping?.filterKey;

  // Resolve the availability facet for this question's granularity. Bail
  // (unfiltered) for non-concentration questions or when the relevant facet is
  // empty — the fail-open contract: never hide options just because
  // availability couldn't be determined.
  let availableValues: Set<string>;

  if (filterKey === FORM_FUNNEL_SUBINDUSTRY_FILTER_KEY) {
    if (!availability.subindustries?.length) return question;
    availableValues = new Set(availability.subindustries);
  } else if (filterKey === FORM_FUNNEL_VOCATION_FILTER_KEY) {
    if (!availability.vocationIds?.length) return question;
    availableValues = new Set(availability.vocationIds);
  } else {
    return question;
  }

  let matchedConceptOption = false;
  const filteredOptions = question.options?.filter((option) => {
    const conceptValue = getConcentrationOptionConcept(question, option.value);
    // Options that don't map to a concept (e.g. an "I'm not sure" choice) are
    // never availability-driven, so always keep them.
    if (!conceptValue) return true;

    const isAvailable = availableValues.has(conceptValue);
    if (isAvailable) matchedConceptOption = true;
    return isAvailable;
  });

  if (!matchedConceptOption || !filteredOptions?.length) {
    return question;
  }

  return {
    ...question,
    options: filteredOptions,
  };
}

/**
 * True when the availability query should be in flight for the current funnel
 * position: the next answer-collecting step the user will actually see —
 * skipping hidden steps exactly as navigation does — contains a concentration
 * question. Fetching any earlier is wasted work: every intervening visible
 * answer changes the query variables and invalidates the result — a discarded
 * response and a duplicate DreamSearch projection on an unauthenticated,
 * high-volume path (ENG-7917). Interstitials and conditionally hidden steps
 * collect nothing, so a prefetch across them is guaranteed to still match when
 * the concentration step renders. Visibility must be judged with the same
 * answers navigation will use: a hidden question step must not block the
 * prefetch (navigation skips it), and a hidden concentration question must not
 * trigger one (it is never rendered) — whether its whole step is hidden or a
 * visible page group hides just it while a sibling keeps the step visible.
 *
 * The question-visibility callback receives the step index because "hidden" is
 * only trustworthy for cross-step dependencies: DynamicFormStep reveals
 * questions from live Formik values, which reach the parent's answers only on
 * step submit — so a question whose dependency is a sibling in its own step
 * can be revealed mid-step while the parent still judges it hidden. Callers
 * must fail open (treat as visible) for that shape, so the reveal finds a warm
 * cache instead of rendering unfiltered options.
 *
 * The steps parameter mirrors FormFunnelPage's FunnelStep union structurally,
 * so a new step variant breaks this signature loudly — it must then decide
 * whether it is transparent to the prefetch (skipped) or answer-collecting
 * (a fetch boundary), not silently default to either.
 */
export function shouldFetchAvailability<Q extends QuestionLike>(
  steps: ReadonlyArray<{ type: "question"; questions: readonly Q[] } | { type: "interstitial" }>,
  currentStep: number,
  isStepVisible: (stepIndex: number) => boolean,
  isQuestionVisible: (question: Q, stepIndex: number) => boolean
): boolean {
  for (let index = currentStep; index < steps.length; index++) {
    const step = steps[index];
    if (step.type !== "question" || !isStepVisible(index)) continue;
    return step.questions.some(
      (question) => isQuestionVisible(question, index) && isDynamicConcentrationQuestion(question)
    );
  }
  return false;
}

/**
 * True when a question's answer provably cannot change the availability
 * result: the server derives no filter from its filter key — either it has
 * none (`processFormAnswers` skips those after the metadata pass) or the key
 * is one METADATA_ONLY_KEYS is built from (`AVAILABILITY_INERT_FILTER_KEYS`)
 * — and no metadata from the question. Metadata flows through exactly two
 * doors, both checked here: an *enabled* CMS mapping to one of
 * `AVAILABILITY_METADATA_CUSTOMER_FIELDS`, or the server's code-fallback
 * table keyed by the canonical `AVAILABILITY_METADATA_FALLBACK_FIELD_KEYS`
 * fieldKeys (consulted when the CMS mapping is absent or disabled) — matched
 * via `getAnswerKey`, so the `QuestionConfig` shape (answer key on `id`, no
 * `fieldKey`) takes the same door. Presence
 * of the mapping group proves nothing — Payload materializes it on every
 * question document (disabled ⇒ `{ enabled: null }`) — and enabled mappings
 * to other customer fields (e.g. `timeframePreference`, `email`) populate the
 * lead, not matching.
 */
const isAvailabilityInertQuestion = (question: QuestionLike): boolean => {
  const filterKey = question.filterMapping?.filterKey;
  if (filterKey && !AVAILABILITY_INERT_FILTER_KEY_SET.has(filterKey)) return false;
  const mapping = question.customerFieldMapping;
  if (mapping?.enabled && mapping.customerField) {
    // An enabled mapping overrides the fallback table server-side
    // (getMappedCustomerField) — a fallback-listed fieldKey redirected to a
    // non-availability field derives nothing, so the mapping decides alone.
    return !AVAILABILITY_METADATA_CUSTOMER_FIELD_SET.has(mapping.customerField);
  }
  const answerKey = getAnswerKey(question);
  return !(answerKey && AVAILABILITY_METADATA_FALLBACK_FIELD_KEY_SET.has(answerKey));
};

/**
 * Builds the `answers` variable for the `getFormFunnelAvailableConcentrations`
 * query by dropping every answer the server provably ignores when deriving
 * the facets: the user's own concentration picks (excluded server-side via
 * `excludeConcentrationFilters`) and availability-inert answers
 * (`isAvailabilityInertQuestion`). Sending either would only fragment the
 * client cache — the answer changes the request key while the server returns
 * the same facets. Dropping the inert tail is what lets the submit-time
 * backstop hit the cache: post-concentration steps are typically
 * timeframe/likelihood questions, so without it every funnel completion
 * re-paid the availability round trip for a guaranteed-identical result.
 *
 * FormFunnelPage reads availability twice — a live query during the questions
 * and a backstop just before matching — and Apollo only serves the second from
 * cache when both produce byte-identical variables. Both reads therefore build
 * their variables through `buildConcentrationAvailabilityVariables`
 * (useFormFunnelQueries), which wraps this projection in the full envelope;
 * hand-rolled copies would silently drift into two cache keys and double the
 * traffic on an unauthenticated, high-volume path (ENG-7917).
 */
export function buildConcentrationAvailabilityAnswers(
  questions: readonly QuestionLike[],
  answers: Record<string, unknown>
): Record<string, unknown> {
  const droppedAnswerKeys = new Set(
    questions
      .filter(
        (question) =>
          isFormFunnelConcentrationQuestion(question) || isAvailabilityInertQuestion(question)
      )
      .map(getAnswerKey)
      .filter((key): key is string => !!key)
  );
  if (!droppedAnswerKeys.size) return answers;

  return Object.fromEntries(Object.entries(answers).filter(([key]) => !droppedAnswerKeys.has(key)));
}

/**
 * True when a fresh availability result could still change the answers — i.e.
 * some concentration question holds an answer that
 * `removeUnavailableQuestionAnswers` is capable of stripping once its options
 * have been filtered.
 *
 * Lets the submit-time backstop stay off the wire when it provably cannot
 * matter. An unanswered concentration question has nothing to strip, and an
 * answer on an option that maps to no subindustry/vocation (an "I'm not sure"
 * choice) is kept by `filterConcentrationOptionsByAvailability` under every
 * possible availability result — so in both cases the query would only re-derive
 * the answers we already hold (ENG-7917).
 */
export function hasScrubbableConcentrationAnswer<T extends QuestionWithOptionsLike>(
  questions: readonly T[],
  answers: Record<string, unknown>
): boolean {
  return questions.some((question) => {
    if (!isDynamicConcentrationQuestion(question)) return false;
    const answerKey = getAnswerKey(question);
    if (!answerKey || !question.options?.length) return false;

    const answer = answers[answerKey];
    if (answer === undefined || answer === null || answer === "") return false;

    const optionValues = new Set(question.options.map((option) => String(option.value)));
    return (Array.isArray(answer) ? answer : [answer]).some((value) => {
      const rawValue = String(value);
      // An answer that is not one of the question's own options is stripped
      // regardless of availability, so the backstop still has work to do.
      if (!optionValues.has(rawValue)) return true;
      if (isOptionTargetQuestion(question)) return true;
      return getConcentrationOptionConcept(question, rawValue) !== null;
    });
  });
}

export function removeUnavailableQuestionAnswers<T extends QuestionWithOptionsLike>(
  questions: readonly T[],
  answers: Record<string, unknown>
): Record<string, unknown> {
  let nextAnswers: Record<string, unknown> | null = null;

  for (const question of questions) {
    if (isFormFunnelConcentrationQuestion(question) && !isDynamicConcentrationQuestion(question)) {
      continue;
    }
    const answerKey = getAnswerKey(question);
    // An empty filtered degree list still invalidates its saved answer.
    // Concentrations retain their existing empty-list fail-open behavior.
    if (
      !answerKey ||
      !question.options ||
      (!question.options.length && question.filterMapping?.filterKey !== "program_level_in")
    )
      continue;
    const answer = answers[answerKey];
    if (answer === undefined || answer === null || answer === "") continue;

    const availableValues = new Set(question.options.map((option) => String(option.value)));
    if (Array.isArray(answer)) {
      const availableAnswerValues = answer.filter((value) => availableValues.has(String(value)));
      if (availableAnswerValues.length === answer.length) continue;

      nextAnswers ??= { ...answers };
      if (availableAnswerValues.length) {
        nextAnswers[answerKey] = availableAnswerValues;
      } else {
        delete nextAnswers[answerKey];
      }
      continue;
    }

    if (availableValues.has(String(answer))) continue;

    nextAnswers ??= { ...answers };
    delete nextAnswers[answerKey];
  }

  return nextAnswers ?? answers;
}

/**
 * A saved interest can outlive a removed CMS choice. Clear only unknown answer
 * identities here, independent of availability or the show-all setting. The
 * caller must return the learner to that question before attempting matching.
 */
export function removeUnknownOptionTargetAnswers<T extends QuestionWithOptionsLike>(
  questions: readonly T[],
  answers: Record<string, unknown>
): Record<string, unknown> {
  let nextAnswers: Record<string, unknown> | null = null;
  for (const question of questions) {
    if (!isOptionTargetQuestion(question)) continue;
    const answerKey = getAnswerKey(question);
    if (!answerKey) continue;
    const answer = answers[answerKey];
    if (answer === undefined || answer === null || answer === "") continue;
    const optionCounts = new Map<string | number | boolean, number>();
    for (const { value } of question.options ?? []) {
      optionCounts.set(value, (optionCounts.get(value) ?? 0) + 1);
    }

    const isMultiselect = question.type === "multiselect";
    const isArrayAnswer = Array.isArray(answer);
    const knownValues = new Set<string>();
    let malformed = isArrayAnswer && !isMultiselect;
    for (const value of isArrayAnswer ? answer : [answer]) {
      if (typeof value !== "string") {
        malformed = true;
        break;
      }
      if (optionCounts.get(value) === 1) knownValues.add(value);
    }

    // Keep the original object when no cleanup or scalar-to-array conversion is needed.
    const unchanged =
      !malformed &&
      (isArrayAnswer
        ? knownValues.size === answer.length
        : !isMultiselect && knownValues.size === 1);
    if (unchanged) continue;

    nextAnswers ??= { ...answers };
    if (isMultiselect && !malformed && knownValues.size) {
      nextAnswers[answerKey] = [...knownValues];
    } else {
      delete nextAnswers[answerKey];
    }
  }
  return nextAnswers ?? answers;
}

/**
 * Resolves a concentration option to the availability concept it is checked
 * against — a subindustry or a vocation ID, depending on the question's
 * granularity. Returns null when the option is not availability-driven: a
 * non-concentration question, or an option with no concept mapping (e.g. an
 * "I'm not sure" choice), which callers always keep.
 */
function getConcentrationOptionConcept(
  question: QuestionLike,
  optionValue: string | number | boolean
): string | null {
  const filterKey = question.filterMapping?.filterKey;
  if (filterKey === FORM_FUNNEL_SUBINDUSTRY_FILTER_KEY) {
    return getOptionSubindustryValue(question, optionValue);
  }
  if (filterKey === FORM_FUNNEL_VOCATION_FILTER_KEY) {
    return getOptionVocationId(question, optionValue);
  }
  return null;
}

function getOptionSubindustryValue(
  question: QuestionLike,
  optionValue: string | number | boolean
): string | null {
  const rawValue = String(optionValue);
  const mappedValue = question.filterMapping?.valueMapping?.[rawValue] ?? rawValue;
  return isVocationSubindustryValue(mappedValue) ? mappedValue : null;
}

/**
 * Resolves a vocation-keyed option to its vocation ID via `valueMapping`
 * (e.g. RADIOLOGY_TECHNICIAN → cli2bzrn939fd3dpbapji5u87). Unlike subindustries,
 * whose enum values are recognizable on their own, a raw vocation ID can't be
 * validated without a lookup — so participation requires an explicit mapping
 * entry (the CMS convention for these questions). Options without one (e.g. an
 * "I'm not sure" choice) return null and are always kept.
 *
 * The mapped ID is trimmed: some CMS mappings carry a stray trailing space
 * (e.g. CONCENTRATION_HEALTH_IND), and the availability facet holds clean DB
 * vocation IDs, so an untrimmed value would never match. Vocation IDs are CUIDs
 * with no meaningful whitespace, so trimming is safe.
 */
function getOptionVocationId(
  question: QuestionLike,
  optionValue: string | number | boolean
): string | null {
  const mapped = question.filterMapping?.valueMapping?.[String(optionValue)]?.trim();
  return mapped ? mapped : null;
}
